/* Theme-side convenience rules. Checkout validation must also be enforced by a Shopify app. */
function cartRewardUpdates(cart, groups) {
  const rewardItems = cart.items.filter(item => item.properties?._cart_reward);
  const spend = cart.items.filter(item => !item.properties?._cart_reward && !groups.some(group => group.variants.includes(String(item.variant_id))))
    .reduce((total, item) => total + item.final_line_price, 0);
  const used = new Set();
  const updates = {};
  for (const item of rewardItems) {
    const name = item.properties._cart_reward;
    const group = groups.find(entry => entry.name === name);
    const eligible = group && group.enabled !== false && spend > 0 && spend >= group.threshold && item.final_line_price === 0 && group.variants.includes(String(item.variant_id)) && !used.has(name);
    const quantity = eligible ? 1 : 0;
    if (eligible) used.add(name);
    if (item.quantity !== quantity) updates[item.key] = quantity;
  }
  return { updates, spend, used };
}
function cartRewardRemovalUpdates(cart, action) {
  return Object.fromEntries(cart.items
    .filter(item => item.properties?._cart_reward === action.group && String(item.variant_id) === action.variant)
    .map(item => [item.key, 0]));
}
if (typeof module !== 'undefined') module.exports = { cartRewardUpdates, cartRewardRemovalUpdates };
if (typeof window !== 'undefined') {
  let queue = Promise.resolve();
  const root = () => window.Shopify?.routes?.root || '/';
  async function request(path, body) {
    const response = await fetch(root() + path, body ? {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body)
    } : { cache: 'no-store' });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || 'Unable to update your cart. Please try again.');
    return data;
  }
  function config() {
    return [...document.querySelectorAll('cart-rewards [data-group]')].map(group => ({
      name: group.dataset.group, enabled: group.dataset.enabled === 'true', threshold: Number(group.dataset.threshold),
      variants: [...group.querySelectorAll('[data-variant][data-available="true"]')].map(card => card.dataset.variant)
    }));
  }
  function error(message) {
    const target = document.querySelector('[data-reward-error]');
    if (target) { target.hidden = !message; target.textContent = message; }
  }
  async function publish() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    const cart = await request('cart.js');
    const ids = [...new Set(sections)].slice(0, 5);
    const response = await fetch(root() + '?sections=' + encodeURIComponent(ids.join(',')), { cache: 'no-store' });
    if (!response.ok) throw new Error('Cart updated. Please reload to see the latest cart.');
    cart.sections = await response.json();
    if (ids.some(id => !cart.sections[id])) { document.dispatchEvent(new CustomEvent('cart:refresh')); return; }
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
  }
  function run(action) {
    const operation = queue.then(async () => {
      error('');
      const groups = config();
      if (!groups.length) return;
      let cart = await request('cart.js');
      let rules = cartRewardUpdates(cart, groups);
      let changed = Object.keys(rules.updates).length > 0;
      if (changed) {
        cart = await request('cart/update.js', { updates: rules.updates });
        rules = cartRewardUpdates(cart, groups);
      }
      if (action?.type === 'remove') {
        const updates = cartRewardRemovalUpdates(cart, action);
        if (Object.keys(updates).length) await request('cart/update.js', { updates });
        action.committed = true;
        // Refresh even if another cart action already removed the selected reward.
        changed = true;
      } else if (action) {
        const group = groups.find(entry => entry.name === action.group);
        if (!group || !group.enabled || !group.variants.includes(action.variant) || rules.spend <= 0 || rules.spend < group.threshold || rules.used.has(group.name)) {
          await publish();
          throw new Error('This reward is not available. Check your cart total and existing selection.');
        }
        await request('cart/add.js', { items: [{ id: Number(action.variant), quantity: 1, properties: { _cart_reward: group.name } }] });
        action.committed = true;
        changed = true;
      }
      if (changed) await publish();
    });
    queue = operation.catch(cause => error(cause.message));
    return operation;
  }
  class CartRewards extends HTMLElement {
    connectedCallback() {
      this.onclick = async event => {
        const scroll = event.target.closest('[data-reward-scroll]');
        if (scroll) {
          const slider = scroll.closest('details').querySelector('.cart-reward__slider');
          const direction = getComputedStyle(slider).direction === 'rtl' ? -1 : 1;
          slider.scrollBy({ left: slider.clientWidth * .9 * Number(scroll.dataset.rewardScroll) * direction, behavior: 'smooth' });
        }
        const button = event.target.closest('[data-reward-add], [data-reward-remove]');
        if (!button || button.disabled) return;
        const group = button.closest('[data-group]');
        const buttons = [...group.querySelectorAll('[data-reward-add], [data-reward-remove]')];
        const previousStates = buttons.map(control => control.disabled);
        // Lock the whole category immediately, including while the request is pending.
        buttons.forEach(control => { control.disabled = true; });
        const action = {
          type: button.hasAttribute('data-reward-remove') ? 'remove' : 'add',
          group: group.dataset.group,
          variant: button.dataset.rewardRemove || button.dataset.rewardAdd
        };
        const originalLabel = button.textContent;
        const originalMinWidth = button.style.minWidth;
        button.style.minWidth = `${button.getBoundingClientRect().width}px`;
        button.textContent = action.type === 'remove' ? 'Removing…' : 'Adding…';
        button.setAttribute('aria-busy', 'true');
        try { await run(action); }
        catch (_) {
          // Keep controls locked after a successful mutation if refreshing fails.
          if (!action.committed && group.isConnected) {
            buttons.forEach((control, index) => { control.disabled = previousStates[index]; });
          }
        }
        finally {
          button.removeAttribute('aria-busy');
          button.textContent = originalLabel;
          button.style.minWidth = originalMinWidth;
        }
      };
      run().catch(() => {});
    }
  }
  if (!customElements.get('cart-rewards')) customElements.define('cart-rewards', CartRewards);
  document.addEventListener('cart:change', () => run().catch(() => {}));
  document.addEventListener('submit', async event => {
    if (!event.submitter || event.submitter.name !== 'checkout' || !config().length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    try { await run(); window.location.assign(root() + 'checkout'); } catch (_) { /* Error announced in drawer. */ }
  }, true);
}
